/* Recreaciones de las pantallas del registro en Cl@ve (AEAT) y de la app Cl@ve.
   Son ilustraciones, no capturas: se dibujan con estilos en línea para mantener la
   estética oficial (gris institucional + naranja Cl@ve) sin depender de imágenes. */
import { CSSProperties, ReactNode } from 'react';

const CV = {
  ink: '#1b1b1b',
  sub: '#555555',
  line: '#dcdcdc',
  soft: '#f4f4f4',
  softer: '#fafafa',
  orange: '#ee7402',
  navy: '#2f4b6e',
  red: '#c8102e',
  blue: '#2563eb',
  sans: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
};

/** Isotipo de Cl@ve: espiral naranja. */
export function ClaveIso({ s = 26 }: { s?: number }) {
  return (
    <div
      style={{
        width: s,
        height: s,
        borderRadius: '50%',
        border: `${s * 0.13}px solid ${CV.orange}`,
        borderRightColor: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        transform: 'rotate(45deg)',
      }}
    >
      <div
        style={{
          width: s * 0.32,
          height: s * 0.32,
          borderRadius: '50%',
          background: CV.orange,
        }}
      />
    </div>
  );
}

/** Marco de móvil para las pantallas de la app. */
export function PhoneFrame({
  children,
  maxWidth = 240,
}: {
  children: ReactNode;
  maxWidth?: number;
}) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth,
        margin: '0 auto',
        borderRadius: 34,
        padding: 10,
        background: '#1b1b1f',
        boxShadow: '0 18px 50px rgba(8, 84, 117, 0.18)',
      }}
    >
      <div
        style={{
          borderRadius: 26,
          overflow: 'hidden',
          background: '#ffffff',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 90,
            height: 18,
            background: '#1b1b1f',
            borderRadius: 999,
            zIndex: 2,
          }}
        />
        {children}
      </div>
    </div>
  );
}

function Head({ title, sub }: { title: string; sub?: string }) {
  return (
    <div
      style={{
        background: CV.softer,
        padding: '24px 24px 26px',
        borderBottom: `1px solid ${CV.line}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <ClaveIso s={24} />
        <span
          style={{
            fontFamily: CV.sans,
            fontWeight: 800,
            fontSize: 15,
            color: CV.orange,
          }}
        >
          Cl@ve
        </span>
      </div>
      <h3
        style={{
          fontFamily: CV.serif,
          fontSize: 22,
          fontWeight: 700,
          color: CV.ink,
          margin: 0,
        }}
      >
        {title}
      </h3>
      {sub && (
        <p
          style={{
            fontFamily: CV.sans,
            fontSize: 13,
            color: CV.sub,
            margin: '8px 0 0',
            lineHeight: 1.5,
          }}
        >
          {sub}
        </p>
      )}
      <div
        style={{
          width: 44,
          height: 5,
          background: CV.red,
          marginTop: 12,
          borderRadius: 2,
        }}
      />
    </div>
  );
}

interface FieldProps {
  label?: string;
  hint?: string;
  value?: string;
  placeholder?: string;
  focus?: boolean;
}

function Field({
  label = undefined,
  hint = undefined,
  value = undefined,
  placeholder = undefined,
  focus = false,
}: FieldProps) {
  return (
    <div style={{ marginBottom: 14, width: '100%' }}>
      {label && (
        <div
          style={{
            fontFamily: CV.sans,
            fontSize: 13,
            fontWeight: 700,
            color: CV.ink,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
      )}
      {hint && (
        <div
          style={{
            fontFamily: CV.sans,
            fontSize: 11.5,
            color: CV.sub,
            marginBottom: 6,
          }}
        >
          {hint}
        </div>
      )}
      <div
        style={{
          height: 40,
          borderRadius: 6,
          background: '#ffffff',
          border: `2px solid ${focus ? CV.blue : CV.line}`,
          boxShadow: focus ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          fontFamily: CV.sans,
          fontSize: 14,
          color: value ? CV.ink : '#9a9a9a',
          fontWeight: value ? 600 : 400,
        }}
      >
        {value || placeholder || ''}
      </div>
    </div>
  );
}

function Btn({
  label,
  kind = 'solid',
  ring = false,
}: {
  label: string;
  kind?: 'solid' | 'ghost';
  ring?: boolean;
}) {
  return (
    <div
      style={{
        display: 'inline-block',
        borderRadius: 999,
        padding: '11px 24px',
        fontFamily: CV.sans,
        fontSize: 14,
        fontWeight: 700,
        background: kind === 'solid' ? CV.navy : CV.soft,
        color: kind === 'solid' ? '#ffffff' : CV.navy,
        border: kind === 'solid' ? 'none' : `1px solid ${CV.line}`,
        boxShadow: ring ? '0 0 0 3px rgba(200, 16, 46, 0.75)' : 'none',
      }}
    >
      {label}
    </div>
  );
}

/** 1 · Página de registro en Cl@ve. */
export function CVRegistro() {
  return (
    <div style={{ background: '#ffffff' }}>
      <Head
        title="Registrarse en Cl@ve"
        sub="Date de alta una sola vez y usa Cl@ve en la Agencia Tributaria, la Seguridad Social, el SEPE y otros organismos."
      />
      <div style={{ padding: 22, fontFamily: CV.sans }}>
        <div style={{ background: CV.soft, borderRadius: 8, padding: 20 }}>
          <h4
            style={{
              fontFamily: CV.serif,
              fontSize: 17,
              fontWeight: 700,
              margin: '0 0 8px',
              color: CV.ink,
            }}
          >
            Antes de empezar, ten a mano
          </h4>
          <ul
            style={{
              fontSize: 13.5,
              color: CV.sub,
              lineHeight: 1.6,
              margin: '0 0 16px',
              paddingLeft: 18,
            }}
          >
            <li>Tu DNI o NIE en vigor.</li>
            <li>Un teléfono móvil que sea solo tuyo.</li>
            <li>Una dirección de correo electrónico.</li>
          </ul>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Btn label="Registrarse en Cl@ve" ring />
            <Btn label="Ya estoy registrado" kind="ghost" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** 2 · Identificación con DNI o NIE. */
export function CVIdent() {
  return (
    <div style={{ background: '#ffffff' }}>
      <Head title="Identifica tu documento" />
      <div style={{ padding: '22px 22px 24px', fontFamily: CV.sans }}>
        <Field
          label="DNI o NIE"
          hint="Sin guiones ni espacios"
          placeholder="00000000A"
          focus
        />
        <Field
          label="Fecha de validez del DNI"
          hint="En el NIE, indica el número de soporte"
          placeholder="dd/mm/aaaa"
        />
        <p
          style={{
            fontSize: 12.5,
            color: CV.sub,
            lineHeight: 1.5,
            margin: '2px 0 16px',
          }}
        >
          Comprobaremos tus datos con el Registro Central. Si no coinciden,
          revisa la fecha del documento.
        </p>
        <Btn label="Continuar" ring />
      </div>
    </div>
  );
}

const VIAS = [
  {
    id: 'video',
    label: 'Videoidentificación con la app Cl@ve',
    meta: 'Alta inmediata',
    on: true,
  },
  {
    id: 'cert',
    label: 'Certificado electrónico o DNIe',
    meta: 'Alta inmediata · nivel avanzado',
    on: false,
  },
  {
    id: 'carta',
    label: 'Carta de invitación por correo',
    meta: 'Llega en unos días',
    on: false,
  },
];

/** 3 · Vías de registro. */
export function CVVias() {
  return (
    <div style={{ background: '#ffffff' }}>
      <Head title="¿Cómo quieres verificar tu identidad?" />
      <div
        style={{
          padding: '20px 22px 24px',
          fontFamily: CV.sans,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {VIAS.map((o) => (
          <div
            key={o.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 16px',
              borderRadius: 12,
              border: `2px solid ${o.on ? CV.red : '#dfe5ea'}`,
              boxShadow: o.on ? '0 0 0 3px rgba(200, 16, 46, 0.16)' : 'none',
              background: '#ffffff',
            }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: o.on ? CV.orange : '#e6ebef',
                flex: 'none',
              }}
            />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: CV.ink }}>
                {o.label}
              </div>
              <div style={{ fontSize: 12, color: CV.sub, marginTop: 2 }}>
                {o.meta}
              </div>
            </div>
            {o.on && (
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  fontWeight: 800,
                  color: CV.red,
                  letterSpacing: '0.05em',
                }}
              >
                MÁS RÁPIDO
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** 4 · Datos de contacto del alta. */
export function CVDatos() {
  return (
    <div style={{ background: '#ffffff' }}>
      <Head title="Tus datos de contacto" />
      <div style={{ padding: '22px 22px 24px', fontFamily: CV.sans }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: CV.ink,
            marginBottom: 4,
          }}
        >
          Teléfono móvil
        </div>
        <div style={{ fontSize: 11.5, color: CV.sub, marginBottom: 6 }}>
          Aquí recibirás el PIN por SMS
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div
            style={{
              width: 58,
              height: 40,
              border: `1px solid ${CV.line}`,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            +34
          </div>
          <div
            style={{
              flex: 1,
              height: 40,
              border: `2px solid ${CV.blue}`,
              borderRadius: 6,
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            662 386 855
          </div>
        </div>
        <Field label="Correo electrónico" value="nombre@correo.com" />
        <div
          style={{
            display: 'flex',
            gap: 9,
            alignItems: 'flex-start',
            background: '#f2f6fb',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 15,
              height: 15,
              border: `2px solid ${CV.navy}`,
              borderRadius: 3,
              background: CV.navy,
              flex: 'none',
              marginTop: 1,
            }}
          />
          <span style={{ fontSize: 11.5, color: CV.ink, lineHeight: 1.45 }}>
            He leído y acepto las condiciones de uso del sistema Cl@ve.
          </span>
        </div>
        <Btn label="Confirmar alta" ring />
      </div>
    </div>
  );
}

/** 5 · PIN obtenido. */
export function CVPin() {
  return (
    <div style={{ background: '#ffffff' }}>
      <Head title="Obtener tu PIN" />
      <div
        style={{
          padding: 22,
          fontFamily: CV.sans,
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 180px' }}>
          <div
            style={{
              fontSize: 13.5,
              color: CV.sub,
              lineHeight: 1.55,
              marginBottom: 14,
            }}
          >
            Pide un PIN cada vez que necesites identificarte. Recibes un código
            de seis dígitos por SMS: es de un solo uso y caduca al terminar el
            trámite.
          </div>
          <Btn label="Obtener PIN" ring />
        </div>
        <div
          style={{
            flex: '0 0 148px',
            background: '#111111',
            borderRadius: 18,
            padding: '16px 14px',
            color: '#ffffff',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              marginBottom: 12,
            }}
          >
            <ClaveIso s={20} />
            <span style={{ color: CV.orange, fontWeight: 800, fontSize: 13 }}>
              Cl@ve
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#aaaaaa' }}>Tu PIN</div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: '0.08em',
              margin: '2px 0 10px',
            }}
          >
            481 259
          </div>
          <div style={{ fontSize: 10.5, color: '#8f8f8f', lineHeight: 1.4 }}>
            Válido durante este trámite
          </div>
        </div>
      </div>
    </div>
  );
}

const APP_SHELL: CSSProperties = {
  background: '#111111',
  color: '#ffffff',
  fontFamily: CV.sans,
  padding: '26px 16px 20px',
  height: 380,
  display: 'flex',
  flexDirection: 'column',
};

function AppShell({ children, title }: { children: ReactNode; title: string }) {
  return (
    <div style={APP_SHELL}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <ClaveIso s={20} />
        <span style={{ color: CV.orange, fontWeight: 800, fontSize: 14 }}>
          Cl@ve
        </span>
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          textAlign: 'center',
          marginBottom: 16,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

const APP_BODY: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 14,
};

/** App Cl@ve · foto del DNI. */
export function CVVidDni() {
  return (
    <AppShell title="Fotografía tu DNI">
      <div style={APP_BODY}>
        <div
          style={{
            width: '86%',
            aspectRatio: '1.58',
            borderRadius: 10,
            border: `2px dashed ${CV.orange}`,
            background: '#1c1c1c',
            display: 'flex',
            alignItems: 'flex-end',
            padding: 10,
            gap: 8,
          }}
        >
          <div
            style={{
              width: 34,
              height: 42,
              borderRadius: 4,
              background: '#2f2f2f',
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              paddingBottom: 4,
            }}
          >
            {[70, 90, 55].map((w) => (
              <div
                key={w}
                style={{
                  height: 5,
                  width: `${w}%`,
                  borderRadius: 3,
                  background: '#2f2f2f',
                }}
              />
            ))}
          </div>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: '#9a9a9a',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          Encaja el anverso dentro del marco.
        </div>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            border: '3px solid #ffffff',
          }}
        />
      </div>
    </AppShell>
  );
}

/** App Cl@ve · vídeo de identificación. */
export function CVVidSelfie() {
  return (
    <AppShell title="Grábate un momento">
      <div style={APP_BODY}>
        <div
          style={{
            width: 128,
            height: 164,
            borderRadius: '50% / 42%',
            border: `2px solid ${CV.orange}`,
            background: '#1c1c1c',
          }}
        />
        <div
          style={{
            fontSize: 11.5,
            color: '#9a9a9a',
            textAlign: 'center',
            lineHeight: 1.4,
            maxWidth: 170,
          }}
        >
          Mira a la cámara y sigue las indicaciones.
        </div>
        <div
          style={{
            background: CV.orange,
            color: '#ffffff',
            borderRadius: 999,
            padding: '10px 26px',
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          Empezar
        </div>
      </div>
    </AppShell>
  );
}

/** App Cl@ve · alta confirmada. */
export function CVVidOk() {
  return (
    <AppShell title="Alta confirmada">
      <div style={{ ...APP_BODY, textAlign: 'center' }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: CV.orange,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Ya tienes Cl@ve</div>
        <div
          style={{
            fontSize: 11.5,
            color: '#9a9a9a',
            lineHeight: 1.45,
            maxWidth: 180,
          }}
        >
          Puedes pedir tu PIN ahora mismo desde la app.
        </div>
      </div>
    </AppShell>
  );
}
